import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE_URL, SHOW_DEBUG_INFO } from '../constants';
import authUtils from '../utils/authUtils';
import MessageFeedback from './MessageFeedback';

const ChatInterface = ({ chatId, onError }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedMode, setSelectedMode] = useState('cropwise_read');
  const [loading, setLoading] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const [sseError, setSseError] = useState(null);
  const [chatAction, setChatAction] = useState(null);
  const [chatInfo, setChatInfo] = useState({
    id: chatId,
    name: '',
    status: 'pending',
    mode: 'cropwise_read',
    updated_at: null,
    created_at: null
  });
  const messagesEndRef = useRef(null);
  const eventSourceRef = useRef(null);

  const chatModes = [
    { value: 'cropwise_read', label: 'Cropwise Read' },
    { value: 'cropwise_write', label: 'Cropwise Write' },
    { value: 'knowledge_base_read', label: 'Knowledge Base Read' }
  ];

  useEffect(() => {
    setChatInfo(prevChatInfo => ({
      ...prevChatInfo,
      id: chatId
    }));
  }, [chatId]);

  // Load chat history on component mount
  useEffect(() => {
    loadChatHistory();
    return () => {
      // Cleanup SSE connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll to bottom when new messages arrive or chat action changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, chatAction]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      setLoading(true);
      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }

      const response = await fetch(`${API_BASE_URL}/chats/${chatId}`, {
        headers: {
          'X-User-Credentials': encodedCredentials,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load chat: ${response.status}`);
      }

      const data = await response.json();
      const history = data.data.history || [];
      const chatData = data.data.chat || {};
      const chatActionData = data.data.chat_action || null;
      
      console.log('Loaded chat data:', chatData);
      console.log('Loaded chat action:', chatActionData);
      console.log('Current chatInfo before loadChatHistory update:', chatInfo);
      
      // Sort messages by created_at in ascending order (oldest first)
      const sortedHistory = history.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      
      // Debug feedbacks
      console.log('Messages with feedbacks:', sortedHistory.map(msg => ({
        id: msg.id,
        role: msg.role,
        feedback: msg.feedback
      })));
      
      setMessages(sortedHistory);
      setChatAction(chatActionData);

      setChatInfo(prevChatInfo => {
        const updatedChatInfo = {
          ...prevChatInfo,
          ...chatData,
          id: chatId
        };
        console.log('Updated chatInfo in loadChatHistory:', updatedChatInfo);
        return updatedChatInfo;
      });
      
      if (chatData.mode) {
        console.log('Updating selectedMode in loadChatHistory from', selectedMode, 'to', chatData.mode);
        setSelectedMode(chatData.mode);
      }
    } catch (err) {
      onError(`Error loading chat history: ${err.message}`);
      console.error('Error loading chat history:', err);
    } finally {
      setLoading(false);
    }
  };

  const connectSSE = () => {
    if (eventSourceRef.current && eventSourceRef.current.readyState === 1) {
      return Promise.resolve();
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const encodedCredentials = authUtils.getEncodedCredentials();
    if (!encodedCredentials) {
      setSseError('No authentication credentials found');
      return Promise.reject(new Error('No authentication credentials found'));
    }

    const eventSource = new EventSource(`${API_BASE_URL}/sse/${chatId}/sse?credentials=${encodeURIComponent(encodedCredentials)}`);

    let opened = false;
    const openPromise = new Promise((resolve, reject) => {
      eventSource.onopen = () => {
        opened = true;
        console.log('SSE connection opened');
        setSseConnected(true);
        setSseError(null);
        resolve();
      };

      eventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        if (!opened) {
          reject(new Error('SSE connection failed'));
        }
        setSseConnected(false);
        setSseError('SSE connection failed');
      };
    });

    eventSource.addEventListener('chat_message', (event) => {
      console.log('SSE chat_message received:', event);
      console.log('SSE event data:', event.data);
      try {
        const data = JSON.parse(event.data);
        console.log('Parsed SSE data:', data);
        setMessages(prev => {
          console.log('Adding message to state:', data);
          const updatedMessages = [...prev, data];

          return updatedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        });
      } catch (err) {
        console.error('Error parsing SSE chat_message:', err);
      }
    });

    eventSource.addEventListener('chat', (event) => {
      console.log('SSE chat event received:', event);
      console.log('SSE chat data:', event.data);
      try {
        const data = JSON.parse(event.data);
        console.log('Parsed chat data:', data);
        console.log('Current chatInfo before update:', chatInfo);
        
        setChatInfo(prevChatInfo => {
          const updatedChatInfo = {
            ...prevChatInfo,
            ...data,
            mode: data.mode || prevChatInfo.mode
          };
          console.log('Updated chatInfo:', updatedChatInfo);
          return updatedChatInfo;
        });
        
        if (data.mode && data.mode !== selectedMode) {
          console.log('Updating selectedMode from', selectedMode, 'to', data.mode);
          setSelectedMode(data.mode);
        }
      } catch (err) {
        console.error('Error parsing SSE chat event:', err);
      }
    });

    eventSource.addEventListener('chat_action', (event) => {
      console.log('SSE chat_action event received:', event);
      console.log('SSE chat_action data:', event.data);
      try {
        if (event.data === 'null' || event.data === null) {
          console.log('Removing chat_action (received null data)');
          setChatAction(null);
          return;
        }
        const data = JSON.parse(event.data);
        console.log('Parsed chat_action data:', data);
        setChatAction(data);
      } catch (err) {
        console.error('Error parsing SSE chat_action event:', err);
      }
    });

    eventSource.addEventListener('ping', (event) => {
      console.log('SSE ping received');
    });


    eventSource.addEventListener('error', (event) => {
      console.log('SSE error event:', event);
      try {
        if (event.data && event.data !== 'undefined') {
          const data = JSON.parse(event.data);
          setSseError(data.message || data);
        } else {
          setSseError('SSE connection error');
        }
      } catch (err) {
        console.error('Error parsing SSE error event:', err);
        setSseError('SSE connection error');
      }
    });

    eventSourceRef.current = eventSource;
    return openPromise;
  };

  // const disconnectSSE = () => {
  //   if (eventSourceRef.current) {
  //     eventSourceRef.current.close();
  //     eventSourceRef.current = null;
  //     setSseConnected(false);
  //     setSseError(null);
  //   }
  // };

  const sendMessage = async () => {
    const trimmedMessage = newMessage.trim();
    if (!trimmedMessage) return;

    if (chatInfo.status === 'processing') {
      console.log('Cannot send message: chat is processing');
      onError('Cannot send message while chat is processing. Please wait for the bot to finish.');
      return;
    }

    try {
      setLoading(true);
      
      if (!sseConnected || !eventSourceRef.current || eventSourceRef.current.readyState !== 1) {
        console.log('Connecting to SSE before sending message');
        try {
          await Promise.race([
            connectSSE(),
            new Promise((resolve) => setTimeout(resolve, 2000))
          ]);
        } catch (e) {
          console.warn('Failed to establish SSE before sending, proceeding anyway:', e);
        }
      }

      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }
      
      const formData = new FormData();
      formData.append('mode', selectedMode);
      formData.append('text', trimmedMessage);

      const response = await fetch(`${API_BASE_URL}/chats/${chatId}/send`, {
        method: 'POST',
        headers: {
          'X-User-Credentials': encodedCredentials,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();
      
      setMessages(prev => {
        const updatedMessages = [...prev, data.data];
        return updatedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });
      setNewMessage('');

      setChatInfo(prevChatInfo => {
        const updatedChatInfo = {
          ...prevChatInfo,
          status: 'processing',
          mode: selectedMode,
          updated_at: new Date().toISOString()
        };
        console.log('Updated chat status to processing locally:', updatedChatInfo);
        return updatedChatInfo;
      });


    } catch (err) {
      onError(`Error sending message: ${err.message}`);
      console.error('Error sending message:', err);

      setChatInfo(prevChatInfo => {
        const updatedChatInfo = {
          ...prevChatInfo,
          status: 'pending'
        };
        console.log('Reset chat status to pending due to error:', updatedChatInfo);
        return updatedChatInfo;
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFeedbackUpdate = (messageId, feedback) => {
    console.log('handleFeedbackUpdate called:', { messageId, feedback });
    setMessages(prevMessages => 
      prevMessages.map(message => 
        message.id === messageId 
          ? { ...message, feedback: feedback }
          : message
      )
    );
  };

  const handleChatActionOptionClick = async (actionId, optionId) => {
    try {
      setLoading(true);
      
      // Ensure SSE connection is established before sending chat_action
      if (!sseConnected || !eventSourceRef.current || eventSourceRef.current.readyState !== 1) {
        console.log('Connecting to SSE before sending chat_action');
        try {
          await Promise.race([
            connectSSE(),
            new Promise((resolve) => setTimeout(resolve, 2000))
          ]);
        } catch (e) {
          console.warn('Failed to establish SSE before sending chat_action, proceeding anyway:', e);
        }
      }
      
      const encodedCredentials = authUtils.getEncodedCredentials();
      if (!encodedCredentials) {
        throw new Error('No authentication credentials found');
      }

      const response = await fetch(`${API_BASE_URL}/chat-actions/`, {
        method: 'POST',
        headers: {
          'X-User-Credentials': encodedCredentials,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action_id: actionId,
          option_id: optionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to select action option: ${response.status}`);
      }

      // Find the selected option text from current chatAction
      const selectedOption = chatAction?.options?.find(opt => opt.id === optionId);
      const optionText = selectedOption?.text || 'Selected option';

      // Add user message to the chat immediately
      const userMessage = {
        id: `temp-${Date.now()}`,
        content: optionText,
        role: 'human',
        mode: selectedOption?.type || selectedMode,
        is_final: null,
        created_at: new Date().toISOString(),
        attachments: [],
        feedback: null
      };

      setMessages(prev => {
        const updatedMessages = [...prev, userMessage];
        return updatedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      });

      // Remove chat action after successful selection
      setChatAction(null);
      
      // Update chat status to processing since agent will process the request
      setChatInfo(prevChatInfo => ({
        ...prevChatInfo,
        status: 'processing',
        mode: selectedOption?.type || prevChatInfo.mode,
        updated_at: new Date().toISOString()
      }));

      // Update selected mode if option has a different type
      if (selectedOption?.type && selectedOption.type !== selectedMode) {
        setSelectedMode(selectedOption.type);
      }
      
      onError && onError(null);
    } catch (err) {
      onError(`Error selecting action option: ${err.message}`);
      console.error('Error selecting action option:', err);
      
      // Reset chat status on error
      setChatInfo(prevChatInfo => ({
        ...prevChatInfo,
        status: 'pending'
      }));
    } finally {
      setLoading(false);
    }
  };

  const getChatTitle = () => {
    let chatTitle = chatInfo.name ? `${chatInfo.name}` : `Chat ${chatId}`;

    if (chatTitle.length > 50) {
      chatTitle = `${chatTitle.slice(0, 50).trim()}...`;
    }


    if (SHOW_DEBUG_INFO) {
      chatTitle = `${chatTitle} (ID: ${chatId})`;
    }
    
    return chatTitle;
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-title">
          <h3>{getChatTitle()}</h3>      
          <div className="chat-status">
            <span className={`status-badge ${chatInfo.status}`}>
              {chatInfo.status === 'processing' && (
                <span className="processing-indicator">⏳</span>
              )}
              {chatInfo.status}
            </span>
            <span className="chat-mode">
              Mode: {chatInfo.mode}
            </span>
            {chatInfo.updated_at && (
              <span className="last-updated">
                Updated: {new Date(chatInfo.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div className="chat-controls">
          <select 
            value={selectedMode} 
            onChange={(e) => setSelectedMode(e.target.value)}
            className="mode-selector"
            disabled={chatInfo.status === 'processing'}
          >
            {chatModes.map(mode => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </select>
          
          {/* <div className="sse-controls">
            <button 
              type="button"
              onClick={sseConnected ? disconnectSSE : connectSSE}
              className={`sse-btn ${sseConnected ? 'connected' : 'disconnected'}`}
            >
              {sseConnected ? 'Disconnect SSE' : 'Connect SSE'}
            </button>
            <span className={`sse-status ${sseConnected ? 'connected' : 'disconnected'}`}>
              {sseConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div> */}
        </div>
      </div>

      {sseError && (
        <div className="sse-error">
          SSE Error: {sseError}
        </div>
      )}

      <div className="messages-container">
        {loading && messages.length === 0 ? (
          <div className="loading">Loading chat history...</div>
        ) : (
          <div className="messages">
            {messages.map((message, index) => (
              <div 
                key={message.id || index} 
                className={`message ${message.role === 'human' ? 'user' : 'ai'}`}
              >
                <div className="message-header">
                  {SHOW_DEBUG_INFO && (
                    <>
                      <span className="message-id">{message.id}</span>
                      <span className="message-mode">{message.mode}</span>
                    </>
                  )}  
                  <span className="message-role">{message.role === 'human' ? 'You' : 'AI'}</span>
                  <span className="message-time">
                    {new Date(message.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <div className="message-content">
                  {message.role === 'ai' ? (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  ) : (
                    message.content
                  )}
                </div>
                {message.attachments && message.attachments.length > 0 && (
                  <div className="message-attachments">
                    {message.attachments.map((attachment, attachmentIndex) => (
                      <div key={attachmentIndex} className="attachment">
                        {attachment.type === 'excel' ? (
                          <div className="excel-attachment">
                            <div className="attachment-header">
                              <span className="attachment-name">{attachment.name}</span>
                              <span className="attachment-type">{attachment.type}</span>
                            </div>
                            <div className="excel-viewer">
                              <iframe
                                src={`https://docs.google.com/gview?url=${encodeURIComponent(attachment.url)}&embedded=true`}
                                width="100%"
                                height="400"
                                frameBorder="0"
                                title={`Excel file: ${attachment.name}`}
                                className="excel-iframe"
                                onError={() => {
                                  console.error('Failed to load Excel file in iframe');
                                }}
                              />
                              <div className="excel-fallback">
                                <p>Excel file preview not available</p>
                                <a 
                                  href={attachment.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="download-btn"
                                >
                                  Download Excel file
                                </a>
                              </div>
                            </div>
                            <div className="attachment-footer">
                              <span className="attachment-size">
                                {(attachment.size / 1024).toFixed(1)} KB
                              </span>
                              <a 
                                href={attachment.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="open-external-btn"
                              >
                                Open in new tab
                              </a>
                            </div>
                          </div>
                        ) : (
                          <div className="other-attachment">
                            <div className="attachment-info">
                              <span className="attachment-name">{attachment.name}</span>
                              <span className="attachment-type">{attachment.type}</span>
                              <span className="attachment-size">
                                {(attachment.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                            <div className="attachment-actions">
                              <a 
                                href={attachment.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="download-btn"
                              >
                                Download
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {message.role === 'ai' && (
                  <MessageFeedback
                    messageId={message.id}
                    existingFeedback={message.feedback}
                    onFeedbackUpdate={(feedback) => handleFeedbackUpdate(message.id, feedback)}
                    isFinal={message.is_final}
                  />
                )}
              </div>
            ))}
            
            {/* Chat Action Buttons - Shows contextual tips for new chats */}
            {chatAction && chatAction.options && chatAction.options.length > 0 && (
              <div className="chat-action-container">
                <div className="chat-action-text">{chatAction.text}</div>
                <div className="chat-action-buttons">
                  {[...chatAction.options]
                    .sort((a, b) => {
                      if (a.type !== b.type) {
                        return (a.type || '').localeCompare(b.type || '');
                      }
                      return (a.text || '').localeCompare(b.text || '');
                    })
                    .map((option) => (
                      <div key={option.id} className="chat-action-row">
                        <button
                          type="button"
                          onClick={() => handleChatActionOptionClick(chatAction.id, option.id)}
                          disabled={loading || chatInfo.status === 'processing'}
                          className="chat-action-button"
                        >
                          {option.text}
                        </button>
                        <span className="chat-action-button-type">{option.type}</span>
                      </div>
                    ))}
                </div>
                {chatAction.type === 'initial' && (
                  <div className="chat-action-hint">
                    Выберите один из предложенных советов для начала работы
                  </div>
                )}
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="message-input">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            chatInfo.status === 'processing' 
              ? "Please wait, bot is processing your message..." 
              : "Type your message here..."
          }
          disabled={loading || chatInfo.status === 'processing'}
          rows={3}
        />
        <button 
          type="button"
          onClick={sendMessage} 
          disabled={loading || !newMessage.trim() || chatInfo.status === 'processing'}
          className="send-btn"
        >
          {loading ? 'Sending...' : chatInfo.status === 'processing' ? 'Processing...' : 'Send'}
        </button>
      </div>
    </div>
  );
};

export default ChatInterface;
